<template>
    <div class="food-v3-container">
        <!-- Stats Header Cards -->
        <FoodStatsNavigation :stats="stats" activeTab="carte" @click="navigateToIndex" />

        <!-- Main White Container -->
        <v-app class="ppv2-bg-white dark:ppv2-bg-dark ppv2-rounded-3xl ppv2-p-6">
            <!-- Header -->
            <div class="d-flex flex-row align-items-center" :style="{ marginTop: '10px', marginBottom: normalizeSize(20, true) }">
                <h1
                    class="ppv2-text-black poppinsRegular uppercaseFirstLetter mb-0 title-header"
                    :style="{ fontSize: normalizeFontSize(26) }"
                >
                    {{ mode === 'edit' ? $t('food.productForm.titleEdit') : $t('food.productForm.titleCreate') }}
                </h1>
            </div>

            <form @submit.prevent="handleSubmit">
                <div class="ppv2-flex ppv2-flex-col md:ppv2-flex-row" :style="{ gap: '2%' }">
                    <!-- LEFT COLUMN -->
                    <div class="ppv2-w-full md:ppv2-w-[63%]">
                        <!-- Main Info Card -->
                        <div
                            class="!ppv2-rounded-[10px] ppv2-border !ppv2-border-black !ppv2-border-opacity-20 ppv2-p-6 ppv2-mb-6 ppv2-shadow-none"
                        >
                            <div class="ppv2-grid ppv2-grid-cols-2" :style="{ gap: normalizeSize(16, true) }">
                                <!-- Name -->
                                <CustomInput
                                    v-model="form.name"
                                    :label="$t('food.productForm.nameLabel')"
                                    :placeholder="$t('food.productForm.namePlaceholder')"
                                    :required="true"
                                    :state="getState('name')"
                                    :errorText="errors.name"
                                    @input="clearError('name')"
                                    customLabelClass="poppinsLight ppv2-text-black"
                                    :customLabelStyle="`font-size: ${normalizeSize(15, true)}`"
                                />
                                <BaseSelect
                                    v-model="form.category_id"
                                    :label="$t('food.productForm.categoryLabel')"
                                    :placeholder="$t('food.productForm.categoryPlaceholder')"
                                    :options="categories"
                                    optionLabel="name"
                                    optionValue="id"
                                    :required="true"
                                    :error="errors.category_id"
                                    @input="clearError('category_id')"
                                    customLabelClass="poppinsLight ppv2-text-black"
                                    :customLabelStyle="`font-size: ${normalizeSize(15, true)}`"
                                />
                            </div>

                            <!-- Description -->
                            <BaseTextarea
                                v-model="form.description"
                                :label="$t('food.productForm.descriptionLabel')"
                                :placeholder="$t('food.productForm.descriptionPlaceholder')"
                                :rows="4"
                                customLabelClass="poppinsLight ppv2-text-black"
                                :customLabelStyle="`font-size: ${normalizeSize(15, true)}`"
                            />
                            <div class="ppv2-mt-4 ppv2-text-center">
                                <CustomInputUpload
                                    v-model="form.image"
                                    :label="$t('food.productForm.imageLabel')"
                                    :enableCropper="true"
                                    :maxSize="15 * 1024 * 1024"
                                />
                            </div>
                        </div>

                        <!-- Tarification Card -->
                        <div
                            class="!ppv2-rounded-[10px] ppv2-border !ppv2-border-black !ppv2-border-opacity-20 ppv2-mb-6 ppv2-shadow-none"
                        >
                            <div class="ppv2-flex ppv2-items-center ppv2-justify-between ppv2-p-6">
                                <h2 class="poppinsSemiBold ppv2-text-black mb-0" :style="{ fontSize: normalizeFontSize(18) }">
                                    {{ $t('food.productForm.pricingTitle') }}
                                </h2>
                            </div>
                            <v-divider class="ppv2-m-0"></v-divider>
                            <div class="ppv2-grid ppv2-grid-cols-2 ppv2-gap-6 ppv2-p-6">
                                <CustomInput
                                    v-model="form.price"
                                    :label="$t('food.sellingPrice')"
                                    placeholder="20.00"
                                    type="number"
                                    step="any"
                                    :rightIcon="$symbolCurrency"
                                    :required="true"
                                    :state="getState('price')"
                                    :errorText="errors.price"
                                    @input="clearError('price')"
                                    customLabelClass="poppinsLight ppv2-text-black"
                                    :customLabelStyle="`font-size: ${normalizeSize(15, true)}`"
                                />
                                <BaseSelect
                                    v-model="form.vat_rate"
                                    :label="$t('food.vat_rate')"
                                    :options="vatRates"
                                    optionLabel="label"
                                    optionValue="value"
                                    placeholder="- Sélectionner -"
                                    :required="true"
                                    :error="errors.vat_rate"
                                    @input="clearError('vat_rate')"
                                    customLabelClass="poppinsLight ppv2-text-black"
                                    :customLabelStyle="`font-size: ${normalizeSize(15, true)}`"
                                />
                            </div>
                        </div>

                        <!-- Options Card -->
                        <div
                            class="!ppv2-rounded-[10px] ppv2-border !ppv2-border-black !ppv2-border-opacity-20 ppv2-mb-6 ppv2-shadow-none"
                        >
                            <div class="ppv2-flex ppv2-justify-between ppv2-items-center ppv2-p-6">
                                <h2 class="poppinsSemiBold ppv2-text-black mb-0" :style="{ fontSize: normalizeFontSize(18) }">
                                    {{ $t('food.productForm.optionsTitle') }}
                                </h2>
                                <button
                                    type="button"
                                    @click="openCreateOptionModal(null)"
                                    class="ppv2-underline ppv2-text-black ppv2-text-sm poppinsMedium ppv2-flex ppv2-items-center ppv2-gap-1"
                                >
                                    {{ $t('food.productForm.createOption') }}
                                    <ExternalLink :size="14" />
                                </button>
                            </div>
                            <v-divider class="ppv2-m-0"></v-divider>
                            <div class="ppv2-p-6">
                                <div
                                    v-for="(option, index) in form.options"
                                    :key="'option-' + index"
                                    class="ppv2-flex ppv2-flex-col ppv2-items-end"
                                    :style="{ gap: normalizeSize(16, true) }"
                                >
                                    <div class="ppv2-w-full ppv2-flex ppv2-items-end" :style="{ gap: normalizeSize(16, true) }">
                                        <div class="ppv2-flex-1">
                                            <CustomInput
                                                v-model="option.name"
                                                :label="$t('food.productForm.nameLabel') + ' ' + (index + 1)"
                                                :placeholder="$t('food.productForm.namePlaceholder')"
                                                customLabelClass="poppinsLight ppv2-text-black"
                                                :customLabelStyle="`font-size: ${normalizeSize(15, true)}`"
                                            />
                                        </div>
                                        <div class="ppv2-flex-1 ppv2-mb-[20px]">
                                            <BaseSelect
                                                v-model="option.attribute_id"
                                                :placeholder="$t('food.productForm.categoryPlaceholder')"
                                                :options="availableOptions"
                                                optionLabel="name"
                                                optionValue="id"
                                                @update:modelValue="option.values = []"
                                                customLabelClass="poppinsLight ppv2-text-black"
                                                :customLabelStyle="`font-size: ${normalizeSize(15, true)}`"
                                            />
                                        </div>
                                        <div
                                            class="ppv2-mb-[20px] ppv2-bg-white ppv2-bg-opacity-5 border_duration_experience ppv2-opacity-75 ppv2-text-black d-flex align-items-center justify-center"
                                            style="width: 42px"
                                        >
                                            <v-btn
                                                @click="removeOption(index)"
                                                class="transparent-btn ppv2-text-black"
                                                depressed
                                                small
                                                outlined
                                            >
                                                <Minus :size="20" />
                                            </v-btn>
                                        </div>
                                    </div>

                                    <!-- Values Selection -->
                                    <div v-if="option.attribute_id" class="ppv2-flex ppv2-flex-col ppv2-w-full ppv2-gap-2">
                                        <label class="ppv2-text-sm poppinsMedium ppv2-text-black ppv2-mb-2 ppv2-block">
                                            {{ $t('food.productForm.availableValues') }}
                                        </label>
                                        <div class="ppv2-grid ppv2-grid-cols-3 ppv2-gap-3">
                                            <div v-for="val in getAttributeValues(option.attribute_id)" :key="val.id">
                                                <BaseCheckbox
                                                    :label="val.key"
                                                    :value="option.values && option.values.includes(val.id)"
                                                    @input="toggleOptionValue(option, val.id)"
                                                    :hasBorderChecked="true"
                                                />
                                            </div>
                                        </div>
                                        <div
                                            v-if="getAttributeValues(option.attribute_id).length === 0"
                                            class="ppv2-text-xs ppv2-text-gray-400 ppv2-italic"
                                        >
                                            {{ $t('food.productForm.noValueConfigured') }}
                                        </div>
                                    </div>
                                </div>

                                <div class="ppv2-flex ppv2-justify-center ppv2-mt-4">
                                    <BaseButton type="button" @click="addOption" variant="secondary" :customFontSize="12">
                                        <Plus :size="12" />
                                        {{ $t('food.productForm.addOption') }}
                                    </BaseButton>
                                </div>
                            </div>
                        </div>

                        <!-- Supplements (Addons) Card -->
                        <div
                            class="!ppv2-rounded-[10px] ppv2-border !ppv2-border-black !ppv2-border-opacity-20 ppv2-mb-6 ppv2-shadow-none"
                        >
                            <div class="ppv2-flex ppv2-justify-between ppv2-items-center ppv2-p-6">
                                <h2 class="poppinsSemiBold ppv2-text-black mb-0" :style="{ fontSize: normalizeFontSize(18) }">
                                    {{ $t('food.productForm.supplementsTitle') }}
                                </h2>
                                <button
                                    type="button"
                                    @click="openAddonModal(null)"
                                    class="ppv2-text-black ppv2-underline ppv2-text-sm poppinsMedium ppv2-flex ppv2-items-center ppv2-gap-1"
                                >
                                    {{ $t('food.productForm.createSupplement') }}
                                    <ExternalLink :size="14" />
                                </button>
                            </div>
                            <v-divider class="ppv2-m-0"></v-divider>
                            <div class="ppv2-p-6">
                                <div
                                    v-for="(addon, index) in form.addons"
                                    :key="'addon-' + index"
                                    class="gestion_creneau ppv2-border-[#545595] ppv2-rounded-[12px] ppv2-text-black ppv2-mb-2 ppv2-px-3"
                                >
                                    <div class="ppv2-flex ppv2-items-end" :style="{ gap: normalizeSize(16, true) }">
                                        <div class="ppv2-flex-1">
                                            <BaseSelect
                                                v-model="addon.id"
                                                placeholder="- Sélectionner un groupe de suppléments -"
                                                :options="availableAddons"
                                                optionLabel="name"
                                                optionValue="id"
                                            />
                                        </div>

                                        <button
                                            type="button"
                                            @click="removeAddon(index)"
                                            class="ppv2-w-[42px] ppv2-h-[42px] ppv2-rounded-lg ppv2-border ppv2-border-gray-300 ppv2-text-gray-500 ppv2-flex ppv2-items-center ppv2-justify-center hover:ppv2-bg-gray-50"
                                        >
                                            <Trash2 :size="18" />
                                        </button>
                                    </div>

                                    <!-- Show Items if selected -->
                                    <div
                                        v-if="addon.id"
                                        class="gestion_creneau ppv2-border-[#545595] ppv2-rounded-[12px] ppv2-text-black ppv2-mt-2 ppv2-px-3"
                                    >
                                        <div
                                            v-if="getAddonItems(addon.id).length > 0"
                                            class="ppv2-grid ppv2-grid-cols-2 ppv2-gap-2"
                                        >
                                            <div
                                                v-for="item in getAddonItems(addon.id)"
                                                :key="item.id"
                                                class="ppv2-flex ppv2-items-center ppv2-gap-2 ppv2-text-sm ppv2-text-gray-600"
                                            >
                                                <div class="ppv2-w-1.5 ppv2-h-1.5 ppv2-rounded-full ppv2-bg-gray-400"></div>
                                                <span class="poppinsMedium">{{ item.name }}</span>
                                                <!-- <span v-if="item.price > 0" class="ppv2-text-gray-400"
                                                >({{ Number(item.price).toFixed(2) }}€)</span
                                            > -->
                                                <span
                                                    v-if="item.is_free"
                                                    class="ppv2-text-green-600 ppv2-text-xs ppv2-bg-green-100 ppv2-px-2 ppv2-py-0.5 ppv2-rounded-full"
                                                    >{{ $t('food.options.included') }}</span
                                                >
                                            </div>
                                        </div>
                                        <div v-else class="ppv2-text-sm ppv2-text-gray-400 poppinsLight ppv2-italic">
                                            {{ $t('food.productForm.noChoiceConfigured') }}
                                        </div>
                                    </div>
                                </div>

                                <div class="ppv2-flex ppv2-justify-center ppv2-mt-4">
                                    <BaseButton @click="addAddon" variant="secondary" :customFontSize="12">
                                        <Plus :size="12" />
                                        {{ $t('food.productForm.addSupplement') }}
                                    </BaseButton>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Right Column -->
                    <div class="ppv2-w-full md:ppv2-w-[35%]">
                        <!-- Actions / Availability Card -->
                        <div
                            class="!ppv2-rounded-[10px] ppv2-border !ppv2-border-black !ppv2-border-opacity-20 ppv2-p-6 ppv2-mb-6 ppv2-shadow-none"
                        >
                            <div class="ppv2-space-y-4">
                                <!-- Activé Toggle -->
                                <BaseSwitch
                                    v-model="form.active"
                                    :label="$t('food.productForm.activeLabel')"
                                    :boxed="true"
                                    :variant="form.active ? 'success' : 'gray'"
                                    customLabelClass="poppinsLight ppv2-text-black"
                                    :customLabelStyle="`font-size: ${normalizeSize(15, true)}`"
                                />

                                <!-- Afficher sur la carte -->
                                <BaseSwitch
                                    v-model="form.show_on_menu"
                                    :label="$t('food.productForm.showOnMenuLabel')"
                                    :boxed="true"
                                    customLabelClass="poppinsLight ppv2-text-black"
                                    :customLabelStyle="`font-size: ${normalizeSize(15, true)}`"
                                />
                                <a
                                    :href="frontUrl ? frontUrl + '/discover/food' : '#'"
                                    target="_blank"
                                    class="ppv2-flex ppv2-items-center ppv2-space-x-2 ppv2-w-full ppv2-border ppv2-px-3 ppv2-py-2 ppv2-border-[#E5E7EB] justify-content-center gap-2"
                                    :style="{ fontSize: normalizeFontSize(14), boxShadow: '0px 2px 4px 0px #00000040' }"
                                >
                                    <span class="ppv2-text-black">
                                        {{ $t('food.productForm.viewOnMap') }}
                                        <ExternalLink :size="16" />
                                    </span>
                                </a>
                            </div>
                        </div>

                        <!-- Stock Management Card -->
                        <div
                            class="!ppv2-rounded-[10px] ppv2-border !ppv2-border-black !ppv2-border-opacity-20 ppv2-mb-6 ppv2-shadow-none"
                        >
                            <div class="ppv2-flex ppv2-items-center ppv2-justify-between ppv2-p-6">
                                <h2 class="poppinsSemiBold ppv2-text-black mb-0" :style="{ fontSize: normalizeFontSize(18) }">
                                    {{ $t('food.productForm.stockTitle') }}
                                </h2>
                                <BaseSwitch
                                    v-model="form.manage_stock"
                                    :label="form.manage_stock ? $t('common.yes') : $t('common.no')"
                                    customLabelClass="poppinsLight ppv2-text-black"
                                    :customLabelStyle="`font-size: ${normalizeSize(14, true)}`"
                                />
                            </div>

                            <v-divider v-if="form.manage_stock" class="ppv2-m-0"></v-divider>
                            <div v-if="form.manage_stock" class="ppv2-p-6">
                                <CustomInput
                                    v-model="form.stock"
                                    :label="$t('food.productForm.stockLabel')"
                                    placeholder="0"
                                    type="number"
                                    customLabelClass="poppinsLight ppv2-text-black"
                                    :customLabelStyle="`font-size: ${normalizeSize(15, true)}`"
                                />
                                <p class="ppv2-text-xs ppv2-text-[#94A3B8] ppv2-mt-1">
                                    {{ $t('food.productForm.stockInfo') }}
                                </p>
                                <div class="ppv2-mt-4">
                                    <BaseSwitch
                                        v-model="form.is_out_of_stock"
                                        :label="$t('food.productForm.isOutOfStockLabel')"
                                        :boxed="true"
                                        :variant="form.is_out_of_stock ? 'danger' : 'gray'"
                                        customLabelClass="poppinsLight ppv2-text-black"
                                        :customLabelStyle="`font-size: ${normalizeSize(15, true)}`"
                                    />
                                    <p class="ppv2-text-xs ppv2-text-[#94A3B8] ppv2-mt-1">
                                        {{ $t('food.productForm.isOutOfStockInfo') }}
                                    </p>
                                </div>
                            </div>
                        </div>

                        <!-- Takeaway Card -->
                        <div
                            class="!ppv2-rounded-[10px] ppv2-border !ppv2-border-black !ppv2-border-opacity-20 ppv2-mb-6 ppv2-shadow-none"
                        >
                            <div class="ppv2-flex ppv2-items-center ppv2-justify-between ppv2-p-6">
                                <h2 class="poppinsSemiBold ppv2-text-black mb-0" :style="{ fontSize: normalizeFontSize(18) }">
                                    {{ $t('food.productForm.takeawayTitle') }}
                                </h2>
                                <BaseSwitch
                                    v-model="form.is_takeaway_enabled"
                                    :label="form.is_takeaway_enabled ? $t('common.yes') : $t('common.no')"
                                    customLabelClass="poppinsLight ppv2-text-black"
                                    :customLabelStyle="`font-size: ${normalizeSize(14, true)}`"
                                />
                            </div>
                            <v-divider class="ppv2-m-0" v-if="form.is_takeaway_enabled"></v-divider>
                            <div v-if="form.is_takeaway_enabled" class="ppv2-p-6">
                                <CustomInput
                                    v-model="form.price_takeaway"
                                    :label="$t('food.sellingPrice')"
                                    placeholder="0.00"
                                    type="number"
                                    step="any"
                                    :rightIcon="$symbolCurrency"
                                    classContainerAdd="ppv2-mb-4"
                                    :state="getState('price_takeaway')"
                                    :errorText="errors.price_takeaway"
                                    @input="clearError('price_takeaway')"
                                    customLabelClass="poppinsLight ppv2-text-black"
                                    :customLabelStyle="`font-size: ${normalizeSize(15, true)}`"
                                />
                                <BaseSelect
                                    v-model="form.vat_rate_takeaway"
                                    :label="$t('food.vat_rate')"
                                    :options="vatRates"
                                    optionLabel="label"
                                    optionValue="value"
                                    :error="errors.vat_rate_takeaway"
                                    @input="clearError('vat_rate_takeaway')"
                                    customLabelClass="poppinsLight ppv2-text-black"
                                    :customLabelStyle="`font-size: ${normalizeSize(15, true)}`"
                                />
                            </div>
                        </div>

                        <!-- Delivery Card -->
                        <div
                            class="!ppv2-rounded-[10px] ppv2-border !ppv2-border-black !ppv2-border-opacity-20 ppv2-mb-6 ppv2-shadow-none"
                        >
                            <div class="ppv2-flex ppv2-items-center ppv2-justify-between ppv2-p-6">
                                <h2 class="poppinsSemiBold ppv2-text-black mb-0" :style="{ fontSize: normalizeFontSize(18) }">
                                    {{ $t('food.productForm.deliveryTitle') }}
                                </h2>
                                <BaseSwitch
                                    v-model="form.is_delivery_enabled"
                                    :label="form.is_delivery_enabled ? $t('common.yes') : $t('common.no')"
                                    customLabelClass="poppinsLight ppv2-text-black"
                                    :customLabelStyle="`font-size: ${normalizeSize(14, true)}`"
                                    :class="{
                                        '!ppv2-text-[#15803d]': form.is_delivery_enabled,
                                        '!ppv2-text-[#94A3B8]': !form.is_delivery_enabled,
                                    }"
                                />
                            </div>
                            <v-divider class="ppv2-m-0" v-if="form.is_delivery_enabled"></v-divider>
                            <div v-if="form.is_delivery_enabled" class="ppv2-p-6">
                                <CustomInput
                                    v-model="form.price_delivery"
                                    :label="$t('food.sellingPrice')"
                                    placeholder="0.00"
                                    type="number"
                                    step="any"
                                    :rightIcon="$symbolCurrency"
                                    classContainerAdd="ppv2-mb-4"
                                    :state="getState('price_delivery')"
                                    :errorText="errors.price_delivery"
                                    @input="clearError('price_delivery')"
                                    customLabelClass="poppinsLight ppv2-text-black"
                                    :customLabelStyle="`font-size: ${normalizeSize(15, true)}`"
                                />
                                <BaseSelect
                                    v-model="form.vat_rate_delivery"
                                    :label="$t('food.vat_rate')"
                                    :options="vatRates"
                                    optionLabel="label"
                                    optionValue="value"
                                    :error="errors.vat_rate_delivery"
                                    @input="clearError('vat_rate_delivery')"
                                    customLabelClass="poppinsLight ppv2-text-black"
                                    :customLabelStyle="`font-size: ${normalizeSize(15, true)}`"
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <div class="ppv2-flex ppv2-flex-col md:ppv2-flex-row" :style="{ gap: '2%' }">
                    <!-- Bottom Actions -->
                    <div class="ppv2-w-full md:ppv2-w-[63%]">
                        <!-- Footer: Retour & Valider -->
                        <div
                            class="ppv2-flex ppv2-justify-between ppv2-items-center"
                            :style="{ marginTop: normalizeSize(24), marginBottom: normalizeSize(40, true) }"
                        >
                            <BackButton type="button" @click="goBack" />
                            <BaseButton
                                variant="primary"
                                :loading="isSubmitting"
                                @click="handleSubmit"
                                :customHeight="48"
                                customClass="!ppv2-min-w-[160px] !ppv2-bg-[#7D2DBD] hover:!ppv2-bg-[#6b26a3]"
                            >
                                {{ isSubmitting ? $t('common.saving') : $t('common.confirm') }}
                            </BaseButton>
                        </div>
                    </div>
                </div>
            </form>
        </v-app>

        <!-- Modals -->
        <AddonModal
            :showAddonModal="showAddonModal"
            :addon="currentAddon"
            :type="addonModalType"
            :vatRates="vatRates"
            @close="closeAddonModal"
            @save="handleSaveAddon"
            :clearError="clearError"
            :getState="getState"
            :getItemState="getItemState"
        />
    </div>
</template>

<script>
import { Plus, Minus, ExternalLink, ArrowLeft, Loader2, Trash2 } from 'lucide-vue'
import CustomInput from '../../Inputs/CustomInput.vue'
import CustomInputUpload from '../../Inputs/CustomInputUpload.vue'
import BaseSelect from '../../Shared/BaseSelect.vue'
import BaseTextarea from '../../Shared/BaseTextarea.vue'
import BaseSwitch from '../../Shared/BaseSwitch.vue'
import BaseButton from '../../Shared/BaseButton.vue'
import BackButton from '../../Shared/BackButton.vue'
import BaseCheckbox from '../../Shared/BaseCheckbox.vue'
import normalizeSize from '../../../utils/normalizeSize'
import FoodStatsNavigation from './FoodStatsNavigation.vue'
import normalizeFontSize from '../../../utils/normalizeFontSize'

export default {
    name: 'BoFoodV3ProductForm',
    components: {
        Plus,
        Minus,
        ExternalLink,
        ArrowLeft,
        Loader2,
        CustomInput,
        CustomInputUpload,
        BaseSelect,
        BaseTextarea,
        BaseSwitch,
        BaseButton,
        BackButton,
        BaseCheckbox,
        FoodStatsNavigation,
        AddonModal: () => import('./AddonModal.vue'), // Lazy load
        Trash2,
    },
    props: {
        mode: {
            type: String,
            default: 'create',
            validator: (val) => ['create', 'edit'].includes(val),
        },
        type: {
            type: String,
            default: 'food',
        },
        productId: {
            type: [String, Number],
            default: null,
        },
        productUuid: {
            type: String,
            default: null,
        },
        establishment: {
            type: Object,
            default: () => ({}),
        },
        frontUrl: {
            type: String,
            default: '',
        },
    },
    data() {
        return {
            isLoading: true,
            isSubmitting: false,
            form: {
                name: '',
                category_id: null,
                description: '',
                image: null,
                price: '',
                vat_rate: null,
                price_takeaway: '',
                vat_rate_takeaway: null,
                price_delivery: '',
                vat_rate_delivery: null,
                active: true,
                show_on_menu: true,
                is_takeaway_enabled: false,
                is_delivery_enabled: false,
                manage_stock: false,
                stock: 0,
                is_out_of_stock: false,
                options: [],
                addons: [], // Selected addons (supplements)
                // supplements: [], // Legacy removed
                // boissons: [], // Legacy removed
            },
            errors: {},
            stats: {
                products: 0,
                categories: 0,
                packs: 0,
                options: 0,
                tables: 0,
            },
            categories: [],
            availableOptions: [],
            availableAddons: [], // List of all addon categories
            showAddonModal: false,
            addonModalType: 'addon', // Default type
            currentAddon: null,
        }
    },
    computed: {
        vatRates() {
            // Get VAT rates from establishment data
            const tvas = this.establishment?.pays?.tvas || []
            if (tvas.length > 0) {
                return tvas.map((tva) => ({
                    value: parseFloat(tva.pourcentage),
                    label: `${parseFloat(tva.pourcentage)}%`,
                }))
            }
            return []
        },
    },
    watch: {
        'form.active'(newValue) {
            // If product is deactivated, also hide from menu
            if (!newValue) {
                this.form.show_on_menu = false
            }
        },
    },
    async mounted() {
        await this.fetchInitialData()

        // Remove white background from parent container
        const mainContainer = document.getElementById('main-container')
        if (mainContainer) {
            mainContainer.classList.add('food-v3-no-bg')
        }
    },
    beforeDestroy() {
        const mainContainer = document.getElementById('main-container')
        if (mainContainer) {
            mainContainer.classList.remove('food-v3-no-bg')
        }
    },
    watch: {
        'form.manage_stock'(val) {
            if (!val) {
                this.form.is_out_of_stock = false
            }
        },
    },
    methods: {
        normalizeFontSize,
        normalizeSize,
        clearError(field) {
            if (this.errors[field]) {
                this.$delete(this.errors, field)
            }
        },
        getItemState(field, index) {
            if (this.errors.items?.[index]?.[field]) return 'error'
            return 'default'
        },
        getState(field) {
            if (this.errors[field]) return 'error'
            if (this.form[field]) return 'valid'
            return 'default'
        },
        async fetchInitialData() {
            try {
                const [statsRes, categoriesRes, optionsRes, addonsRes] = await Promise.all([
                    axios.get('/api/v3/food/stats'), // Keeping stats on API for now if not moved, or move it? Stats seems shared.
                    axios.get('/back-office/v3/food/categories/fast-list'),
                    axios.get('/back-office/v3/food/options/list'),
                    axios.get('/back-office/v3/food/addons/list?itemsPerPage=100'), // Fetch all for dropdown
                ])

                this.stats = statsRes.data.data || this.stats
                this.categories = categoriesRes.data.data || []
                this.availableOptions = optionsRes.data.data || []
                this.availableAddons = addonsRes.data.data || []

                // If edit mode, fetch product data
                if (this.mode === 'edit' && this.productUuid) {
                    await this.fetchProductData()
                }
            } catch (error) {
                this.$toast.error(error.response.data.message)
            } finally {
                this.isLoading = false
            }
        },

        async fetchProductData() {
            try {
                const response = await axios.get(`/back-office/v3/food/show/${this.productUuid}`)
                if (response.data.success) {
                    const data = response.data.data
                    this.form = {
                        name: data.name || '',
                        category_id: data.category_id || null, // Ensure null if undefined/missing
                        description: data.description || '',
                        image: data.image,
                        price: data.price != null ? parseFloat(data.price).toFixed(2) : '',
                        vat_rate: data.vat_rate != null ? parseFloat(data.vat_rate) : null,
                        price_takeaway: data.price_takeaway != null ? parseFloat(data.price_takeaway).toFixed(2) : '',
                        vat_rate_takeaway: data.vat_rate_takeaway != null ? parseFloat(data.vat_rate_takeaway) : null,
                        price_delivery: data.price_delivery != null ? parseFloat(data.price_delivery).toFixed(2) : '',
                        vat_rate_delivery: data.vat_rate_delivery != null ? parseFloat(data.vat_rate_delivery) : null,
                        active: data.active,
                        show_on_menu: data.show_on_menu ?? true,
                        is_takeaway_enabled: !!data.price_takeaway,
                        is_delivery_enabled: !!data.price_delivery,
                        manage_stock: data.manage_stock ?? false,
                        stock: data.stock ?? 0,
                        is_out_of_stock: data.is_out_of_stock ?? false,

                        // Map Attributes/Options
                        // Map Attributes/Options
                        options: (Array.isArray(data.options) ? data.options : []).map((opt) => ({
                            name: opt.custom_name || '',
                            attribute_id: opt.id,
                            values: (Array.isArray(opt.values) ? opt.values : [])
                                .filter((v) => v.selected) // Only keep selected values
                                .map((v) => v.id),
                            custom_prices: (Array.isArray(opt.values) ? opt.values : [])
                                .filter((v) => v.selected)
                                .reduce((acc, v) => ({ ...acc, [v.id]: v.price }), {}),
                        })),

                        // Map Addons
                        addons: (Array.isArray(data.addons) ? data.addons : []).map((addon) => ({
                            id: addon.id,
                        })),
                    }
                }
            } catch (error) {
                this.$toast.error(error.response.data.message)
                console.error('Error fetching product data:', error)
            }
        },

        addOption() {
            this.form.options.push({
                name: '',
                attribute_id: null,
                values: [],
                custom_prices: {},
                price: 0,
            })
        },

        getAttributeValues(attributeId) {
            const attribute = this.availableOptions.find((opt) => opt.id === attributeId)
            return attribute ? attribute.values || [] : []
        },

        removeOption(index) {
            this.form.options.splice(index, 1)
        },

        openCreateOptionModal() {
            this.currentAddon = null // New creation
            this.addonModalType = 'option' // New data property to track modal type
            this.showAddonModal = true
        },

        // Addon Methods
        addAddon() {
            // Add placeholder for selection
            this.form.addons.push({
                id: null,
                name: '',
            })
        },

        removeAddon(index) {
            this.form.addons.splice(index, 1)
        },

        closeAddonModal() {
            this.showAddonModal = false
            this.currentAddon = null
        },

        handleSaveAddon(newItem) {
            if (this.addonModalType === 'option') {
                // Handle Option (Attribute) Update
                const index = this.availableOptions.findIndex((o) => o.id === newItem.id)
                if (index !== -1) {
                    this.availableOptions.splice(index, 1, newItem)
                } else {
                    this.availableOptions.push(newItem)
                }

                // Update custom_prices in form.options if this option is selected
                this.form.options.forEach((opt) => {
                    if (opt.attribute_id === newItem.id) {
                        // Update custom_prices with new prices from modal
                        if (newItem.values) {
                            newItem.values.forEach((v) => {
                                opt.custom_prices[v.id] = v.price || 0
                            })
                        }
                    }
                })
            } else {
                // Handle Addon Update
                const index = this.availableAddons.findIndex((a) => a.id === newItem.id)
                if (index !== -1) {
                    this.availableAddons.splice(index, 1, newItem)
                } else {
                    this.availableAddons.push(newItem)
                }

                // If we were editing a selected addon in the form, update it there too
                const formIndex = this.form.addons.findIndex((a) => a.id === newItem.id)
                if (formIndex !== -1) {
                    this.form.addons.splice(formIndex, 1, newItem)
                }
            }
        },

        getAddonItems(addonId) {
            const addon = this.availableAddons.find((a) => a.id === addonId)
            return addon ? addon.items || [] : []
        },

        async handleSubmit() {
            this.errors = {}
            this.isSubmitting = true

            try {
                const formData = new FormData()

                // Append form fields
                formData.append('name', this.form.name)
                formData.append('category_id', this.form.category_id)
                formData.append('description', this.form.description || '')
                formData.append('price', this.form.price)
                formData.append('vat_rate', this.form.vat_rate)
                formData.append('active', this.form.active ? 1 : 0)
                formData.append('show_on_menu', this.form.show_on_menu ? 1 : 0)
                formData.append('is_takeaway_enabled', this.form.is_takeaway_enabled ? 1 : 0)
                formData.append('is_delivery_enabled', this.form.is_delivery_enabled ? 1 : 0)
                formData.append('manage_stock', this.form.manage_stock ? 1 : 0)
                if (this.form.manage_stock) {
                    formData.append('stock', this.form.stock || 0)
                }
                formData.append('is_out_of_stock', this.form.is_out_of_stock ? 1 : 0)

                if (this.form.is_takeaway_enabled) {
                    formData.append('price_takeaway', this.form.price_takeaway || this.form.price)
                    formData.append('vat_rate_takeaway', this.form.vat_rate_takeaway || this.form.vat_rate)
                }

                if (this.form.is_delivery_enabled) {
                    formData.append('price_delivery', this.form.price_delivery || this.form.price)
                    formData.append('vat_rate_delivery', this.form.vat_rate_delivery || this.form.vat_rate)
                }

                // Handle image
                if (this.form.image instanceof File) {
                    formData.append('image', this.form.image)
                } else if (this.form.image === null) {
                    formData.append('remove_image', '1')
                }

                // Handle options
                if (this.form.options.length > 0) {
                    const optionsToSend = this.form.options.map((opt) => {
                        const availableAttr = this.availableOptions.find((a) => a.id === opt.attribute_id)
                        const valuesWithPrices = (opt.values || []).map((valId) => {
                            let price = 0
                            // Ensure custom_prices exists
                            opt.custom_prices = opt.custom_prices || {}

                            // 1. Try custom_prices (from DB or updated by Modal)
                            if (opt.custom_prices && opt.custom_prices[valId] !== undefined) {
                                price = opt.custom_prices[valId]
                            }
                            // 2. Fallback to availableOptions (session)
                            else if (availableAttr && availableAttr.values) {
                                const valObj = availableAttr.values.find((v) => v.id === valId)
                                if (valObj && valObj.price !== undefined) {
                                    price = valObj.price
                                }
                            }
                            return { id: valId, price: parseFloat(price) }
                        })

                        return {
                            name: opt.name,
                            attribute_id: opt.attribute_id,
                            values: valuesWithPrices,
                        }
                    })
                    formData.append('options', JSON.stringify(optionsToSend))
                } else {
                    formData.append('options', JSON.stringify([]))
                }

                // Handle addons (send IDs)
                if (this.form.addons && this.form.addons.length > 0) {
                    // Filter out null IDs
                    const addonIds = this.form.addons.filter((a) => a && a.id).map((a) => a.id)

                    // Laravel expects array like addons[0]=1, addons[1]=2
                    addonIds.forEach((id, index) => {
                        formData.append(`addons[${index}]`, id)
                    })
                } else {
                    // Send empty addons to clear them
                    // Using 'addons' without index might be interpreted as string "" or array by Laravel depending on middleware
                    // Safer to not rely on index if empty?
                    // Or send explicit empty array?
                    // In PHP: $request->addons will be null if not sent.
                    // If sent as addons="", likely string.
                    // We will handle this in Controller.
                    formData.append('addons', '')
                }

                let response
                if (this.mode === 'edit') {
                    response = await axios.post(`/back-office/v3/food/update/${this.productUuid}`, formData, {
                        headers: { 'Content-Type': 'multipart/form-data' },
                    })
                } else {
                    response = await axios.post('/back-office/v3/food/store', formData, {
                        headers: { 'Content-Type': 'multipart/form-data' },
                    })
                }

                if (response.data.success) {
                    this.$toast?.success?.(response.data.message || this.$t('food.messages.product_saved_success'))
                    window.location.href = '/back-office/v3/food?tab=carte'
                } else {
                    this.$toast?.error?.(response.data.message || this.$t('food.messages.save_error'))
                }
            } catch (error) {
                this.$toast.error(error.response.data.message)
                if (error.response?.data?.errors) {
                    const rawErrors = error.response.data.errors
                    const flattenedErrors = {}
                    Object.keys(rawErrors).forEach((key) => {
                        flattenedErrors[key] = Array.isArray(rawErrors[key]) ? rawErrors[key][0] : rawErrors[key]
                    })
                    this.errors = flattenedErrors
                }
            } finally {
                this.isSubmitting = false
            }
        },

        goBack() {
            window.history.back()
        },

        toggleOptionValue(option, valId) {
            if (!option.values) {
                option.values = []
            }
            const idx = option.values.indexOf(valId)
            if (idx === -1) {
                option.values.push(valId)
            } else {
                option.values.splice(idx, 1)
            }
        },

        navigateToIndex(tab) {
            window.location.href = `/back-office/v3/food?tab=${tab}`
        },
    },
}
</script>

<style scoped lang="scss">
.food-v3-container {
    // This padding matches the layout
    padding: 0;
}

.statsCard {
    background: transparent;
    border: 2px solid rgba(255, 255, 255, 0.35);
    min-width: 180px;
    flex: 1;
}

.statsCardActive {
    background: white !important;
    border: 1px solid rgba(0, 0, 0, 0.05);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
}

.form-section {
    border-radius: 12px;
}

// Override switch color for green theme
::v-deep .custom-switch-green {
    .bg-purple,
    .border-purple {
        background-color: #16a34a !important; // green-600
        border-color: #16a34a !important;
    }
}

.border_duration_experience {
    position: relative;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    border-radius: 12px;
    border: 1px solid #545595;
    min-height: 40px;
}

.border_duration_experience label {
    font-family: 'Poppins Regular', sans-serif;
}

.transparent-btn {
    border-radius: 12px !important;
    width: 100% !important;
    height: 100% !important;
    box-shadow: none !important;
    position: absolute;
    left: 0;
    top: 0;
    border: none;
    min-width: 40px !important;
}

.transparent-btn:hover {
    background: #d9d9d91a !important;
}
</style>

<!-- Non-scoped style to override parent container background -->
<style lang="scss">
// Override the main-container white background for Food V3 module
// Using class added via JavaScript for better browser compatibility
#main-container.food-v3-no-bg {
    background: transparent !important;
    border-radius: 0px;
}

// Also support the :has() selector for modern browsers
#main-container:has(.food-v3-container) {
    background: transparent !important;
}

// Dark mode support
.dark #main-container.food-v3-no-bg,
.dark #main-container:has(.food-v3-container) {
    background: transparent !important;
}

.gestion_creneau {
    background-color: #1a1f370d;
    padding: 10px 12px;
}

.dark .gestion_creneau {
    background-color: rgba(255, 255, 255, 0.05);
}
</style>
